import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Organisation {
  id: string;
  name: string;
  registrationNumber?: string;
  registrationAuthority?: string;
  businessType: string;
  status: string;
  createdAt?: string;
  memberCount?: number;
  agentCount?: number;
  apiKeyProfileCount?: number;
  pendingInvitationCount?: number;
}

export interface OrganisationMembership extends Organisation {
  roleId?: string;
  roleName?: string;
  membershipStatus?: string;
}

export interface Member {
  id: string;
  userId: string;
  orgId: string;
  roleId: string;
  roleName?: string;
  status: string;
  joinedDate: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface Invitation {
  id: string;
  orgId: string;
  inviteeEmail: string;
  invitedBy: string;
  inviteCode?: string;
  roleId: string;
  roleName?: string;
  role?: { name?: string };
  status: string;
  sentAt: string;
  expiresAt: string;
}

export interface InvitePreview {
  orgName: string;
  inviterName: string;
  roleName: string;
  inviteeEmail: string;
  status: string;
  expiresAt: string;
}

export interface Role {
  id: string;
  name: string;
  scope: string;
  isSystemRole: boolean;
  orgId?: string | null;
  permissionsConfig?: Record<string, unknown> | string;
  memberCount?: number;
}

export interface CreateRoleRequest {
  name: string;
  permissionsConfig: Record<string, unknown>;
}

export interface UpdateRoleRequest {
  name?: string;
  permissionsConfig?: Record<string, unknown>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface NotificationPreferences {
  productUpdates: boolean;
  billingAlerts: boolean;
  agentActivity: boolean;
  marketing: boolean;
}

export interface UserPreferences {
  theme: 'light' | 'dark';
  notifications: NotificationPreferences;
}

export type UpdatePreferencesRequest = {
  theme?: 'light' | 'dark';
  notifications?: Partial<NotificationPreferences>;
};

function toPreferencesBody(body: UpdatePreferencesRequest): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (body.theme !== undefined) {
    payload.theme = body.theme;
  }
  if (body.notifications) {
    const n = body.notifications;
    const notifications: Record<string, boolean> = {};
    if (n.productUpdates !== undefined) notifications.product_updates = n.productUpdates;
    if (n.billingAlerts !== undefined) notifications.billing_alerts = n.billingAlerts;
    if (n.agentActivity !== undefined) notifications.agent_activity = n.agentActivity;
    if (n.marketing !== undefined) notifications.marketing = n.marketing;
    payload.notifications = notifications;
  }
  return payload;
}

export const portalApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreferences: builder.query<UserPreferences, void>({
      query: () => '/portal/preferences',
      transformResponse: (response: unknown) => normalizeKeys<UserPreferences>(response),
      providesTags: ['Preferences'],
    }),

    updatePreferences: builder.mutation<UserPreferences, UpdatePreferencesRequest>({
      query: (body) => ({
        url: '/portal/preferences',
        method: 'PUT',
        body: toPreferencesBody(body),
      }),
      transformResponse: (response: unknown) => normalizeKeys<UserPreferences>(response),
      invalidatesTags: ['Preferences'],
    }),

    getOrganisation: builder.query<Organisation, string>({
      query: (orgId) => `/portal/organisations/${orgId}`,
      transformResponse: (response: unknown) => normalizeKeys<Organisation>(response),
      providesTags: ['Organisation'],
    }),

    updateOrganisation: builder.mutation<Organisation, { orgId: string; body: Partial<Organisation> }>({
      query: ({ orgId, body }) => ({
        url: `/portal/organisations/${orgId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Organisation'],
    }),

    createOrganisation: builder.mutation<
      Organisation,
      { name: string; description?: string; businessType: string }
    >({
      query: (body) => ({
        url: '/portal/organisations',
        method: 'POST',
        body: {
          name: body.name,
          business_type: body.businessType,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Organisation>(response),
      invalidatesTags: ['Organisation', 'UserOrganisations'],
    }),

    getUserOrganisations: builder.query<{ organisations: OrganisationMembership[] }, void>({
      query: () => '/portal/organisations',
      transformResponse: (response: unknown) =>
        normalizeKeys<{ organisations: OrganisationMembership[] }>(response),
      providesTags: ['UserOrganisations'],
    }),

    activateOrganisation: builder.mutation<Organisation, string>({
      query: (orgId) => ({
        url: `/portal/organisations/${orgId}/activate`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => normalizeKeys<Organisation>(response),
      invalidatesTags: ['Organisation', 'UserOrganisations', 'Member', 'Agent'],
    }),

    linkGoogle: builder.mutation<{ linked: boolean; email?: string; alreadyLinked?: boolean }, void>({
      query: () => ({
        url: '/portal/auth/link-google',
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ linked: boolean; email?: string; alreadyLinked?: boolean }>(response),
    }),

    getSsoStatus: builder.query<
      {
        emailHost: string;
        isConsumerDomain: boolean;
        domainClaimed: boolean;
        claimedOrgName?: string | null;
        linkPending?: boolean;
      },
      void
    >({
      query: () => '/portal/auth/sso-status',
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          emailHost: string;
          isConsumerDomain: boolean;
          domainClaimed: boolean;
          claimedOrgName?: string | null;
          linkPending?: boolean;
        }>(response),
    }),

    getMembers: builder.query<PaginatedResponse<Member>, { orgId: string; page?: number; pageSize?: number }>({
      query: ({ orgId, page = 1, pageSize = 20 }) =>
        `/portal/organisations/${orgId}/members?page=${page}&page_size=${pageSize}`,
      transformResponse: (response: unknown) => normalizeKeys<PaginatedResponse<Member>>(response),
      providesTags: ['Member'],
    }),

    inviteMember: builder.mutation<
      Invitation,
      { orgId: string; email: string; roleId: string; sendEmail?: boolean }
    >({
      query: ({ orgId, email, roleId, sendEmail = true }) => ({
        url: `/portal/organisations/${orgId}/members`,
        method: 'POST',
        body: { email, role_id: roleId, send_email: sendEmail },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Invitation>(response),
      invalidatesTags: ['Member', 'Invitation'],
    }),

    updateMemberRole: builder.mutation<Member, { memberId: string; roleId: string }>({
      query: ({ memberId, ...body }) => ({
        url: `/portal/members/${memberId}/role`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Member'],
    }),

    removeMember: builder.mutation<void, string>({
      query: (memberId) => ({
        url: `/portal/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Member'],
    }),

    getPendingInvitations: builder.query<Invitation[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/invitations`,
      transformResponse: (response: unknown) => {
        // @handle may wrap list results as { response: [...] , metadata }
        const raw = response as { response?: unknown } | unknown[];
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { response?: unknown }).response)
            ? (raw as { response: unknown[] }).response
            : [];
        return normalizeKeys<Invitation[]>(list);
      },
      providesTags: ['Invitation'],
    }),

    cancelInvitation: builder.mutation<void, string>({
      query: (invitationId) => ({
        url: `/portal/invitations/${invitationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invitation'],
    }),

    resendInvitation: builder.mutation<Invitation, string>({
      query: (invitationId) => ({
        url: `/portal/invitations/${invitationId}/resend`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => normalizeKeys<Invitation>(response),
      invalidatesTags: ['Invitation'],
    }),

    acceptInvitation: builder.mutation<Member, string>({
      query: (inviteCode) => ({
        url: '/portal/invitations/accept',
        method: 'POST',
        body: { invite_code: inviteCode },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Member>(response),
      invalidatesTags: ['Member', 'Organisation', 'Invitation'],
    }),

    getInvitePreview: builder.query<InvitePreview, string>({
      query: (inviteCode) => `/public/invitations/${encodeURIComponent(inviteCode)}`,
      transformResponse: (response: unknown) => normalizeKeys<InvitePreview>(response),
    }),

    getRoles: builder.query<Role[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/roles`,
      transformResponse: (response: unknown) => {
        const raw = response as { response?: unknown } | unknown[];
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { response?: unknown }).response)
            ? (raw as { response: unknown[] }).response
            : [];
        return normalizeKeys<Role[]>(list);
      },
      providesTags: ['Role'],
    }),

    createRole: builder.mutation<
      Role,
      { orgId: string; body: CreateRoleRequest }
    >({
      query: ({ orgId, body }) => ({
        url: `/portal/organisations/${orgId}/roles`,
        method: 'POST',
        body: {
          name: body.name,
          permissions_config: body.permissionsConfig,
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Role>(response),
      invalidatesTags: ['Role'],
    }),

    updateRole: builder.mutation<
      Role,
      { orgId: string; roleId: string; body: UpdateRoleRequest }
    >({
      query: ({ orgId, roleId, body }) => ({
        url: `/portal/organisations/${orgId}/roles/${roleId}`,
        method: 'PUT',
        body: {
          ...(body.name !== undefined ? { name: body.name } : {}),
          ...(body.permissionsConfig !== undefined
            ? { permissions_config: body.permissionsConfig }
            : {}),
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<Role>(response),
      invalidatesTags: ['Role'],
    }),

    deleteRole: builder.mutation<
      { deleted: boolean },
      { orgId: string; roleId: string }
    >({
      query: ({ orgId, roleId }) => ({
        url: `/portal/organisations/${orgId}/roles/${roleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),
  }),
});

export const {
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
  useCreateOrganisationMutation,
  useGetUserOrganisationsQuery,
  useActivateOrganisationMutation,
  useLinkGoogleMutation,
  useGetSsoStatusQuery,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useGetPendingInvitationsQuery,
  useCancelInvitationMutation,
  useResendInvitationMutation,
  useAcceptInvitationMutation,
  useGetInvitePreviewQuery,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = portalApi;
